import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { createAuthAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Star, ExternalLink, ChefHat } from 'lucide-react';
import { Button } from '../ui/button';

const RecipesList = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchLinks = async () => {
      try {
        const api = createAuthAPI();
        const response = await api.get('/client/recipe-links', {
          signal: abortController.signal
        });
        if (isMounted) {
          setLinks(response.data);
          console.log('Recipe links loaded:', response.data);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted) {
          console.error('Error loading recipe links:', error);
          toast.error('Error al cargar recetas');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchLinks();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  const loadLinks = async () => {
    try {
      const api = createAuthAPI();
      const response = await api.get('/client/recipe-links');
      setLinks(response.data);
    } catch (error) {
      toast.error('Error al cargar recetas');
    }
  };

  const rateLink = async (linkId, rating) => {
    try {
      const api = createAuthAPI();
      await api.post('/client/recipe-link-rating', { recipe_link_id: linkId, rating });
      toast.success('Valoración guardada');
      loadLinks();
    } catch (error) {
      toast.error('Error al guardar valoración');
    }
  };

  // Group links by fase
  const linksByFase = links.reduce((acc, link) => {
    if (!acc[link.fase]) acc[link.fase] = [];
    acc[link.fase].push(link);
    return acc;
  }, {});

  const faseNames = {
    '0': 'Fase 0 - Inicio',
    '1a': 'Fase 1a - Con Aves',
    '1b': 'Fase 1b - Con Pescado',
    '1c': 'Fase 1c - Con Atún/Salmón'
  };

  if (loading) {
    return <div>Cargando recetas...</div>;
  }

  return (
    <div className="space-y-8">
      {Object.keys(linksByFase).length === 0 ? (
        <Card className="pharmacy-card">
          <CardContent className="py-12 text-center">
            <ChefHat className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">Aún no hay recetas disponibles</p>
            <p className="text-sm text-gray-500">Tu farmacéutico añadirá recetas pronto</p>
          </CardContent>
        </Card>
      ) : (
        Object.keys(linksByFase).sort().map(fase => (
          <div key={fase}>
            <div className="flex items-center gap-3 mb-4">
              <ChefHat className="h-6 w-6 text-green-600" />
              <h2 className="text-2xl font-bold text-green-800">{faseNames[fase]}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {linksByFase[fase].map(link => (
                <Card key={link.id} className="pharmacy-card recipe-card hover:shadow-lg transition-shadow" data-testid={`recipe-link-${link.id}`}>
                  <CardContent className="pt-6 space-y-4">
                    <div>
                      <h3 className="font-bold text-green-800 mb-2 text-lg">{link.titulo}</h3>
                      <p className="text-xs text-gray-600 mb-3">Fase {link.fase}</p>
                    </div>
                    
                    <Button
                      onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
                      className="pharmacy-button text-white w-full flex items-center justify-center gap-2"
                      data-testid={`open-link-${link.id}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                      Ver Receta
                    </Button>

                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Valorar:</p>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-6 w-6 cursor-pointer transition-all hover:scale-110 ${
                              star <= (link.user_rating || 0)
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            }`}
                            onClick={() => rateLink(link.id, star)}
                            data-testid={`star-${link.id}-${star}`}
                          />
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default RecipesList;